import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { waLink } from "../lib/whatsapp";

const HERO_IMG =
  "https://customer-assets-7cd3h4nn.emergentagent.net/job_advocacy-reimagined/artifacts/231j9zza_composicao.webp";

const LINES = ["Atendimento direto,", "humano e estratégico."];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  return (
    <section
      id="hero"
      ref={ref}
      className="relative pt-32 lg:pt-0 min-h-screen flex items-center bg-cream overflow-hidden [transform:translateZ(0)]"
      data-testid="hero-section"
    >
      <div className="absolute top-0 right-0 w-[55%] h-full bg-cream-dark hidden lg:block" />

      <div className="max-w-7xl mx-auto px-6 lg:px-10 w-full grid lg:grid-cols-2 gap-12 items-center relative z-10 py-16 lg:py-0">
        <div className="order-2 lg:order-1">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-mono text-xs uppercase tracking-[0.25em] text-teal-dark inline-block mb-6"
            data-testid="hero-eyebrow"
          >
            Advocacia Trabalhista
          </motion.span>

          <h1
            className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.2] text-ink"
            data-testid="hero-headline"
          >
            {LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-2 -mb-2">
                <motion.span
                  className="block"
                  initial={{ y: "115%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 0.9, delay: 0.15 + i * 0.13, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.65 }}
            className="mt-7 text-base md:text-lg text-ink-soft leading-relaxed max-w-md"
            data-testid="hero-subheadline"
          >
            Atuamos na defesa de empregados, empresas e outros escritórios —
            com condução artesanal, transparente e próxima em cada caso.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="mt-10 flex flex-wrap items-center gap-5"
          >
            <a
              href={waLink("Olá! Gostaria de falar com as advogadas.")}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-teal-dark text-cream px-7 py-4 font-mono text-xs uppercase tracking-[0.15em] hover:bg-ink transition-colors duration-300"
              data-testid="hero-cta-button"
            >
              Falar com as advogadas
              <ArrowUpRight size={16} />
            </a>
            <button
              onClick={() => document.querySelector("#quem-somos")?.scrollIntoView({ behavior: "smooth" })}
              className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-ink-soft hover:text-ink transition-colors duration-300"
              data-testid="hero-scroll-button"
            >
              Conhecer o escritório
              <ArrowDown size={14} />
            </button>
          </motion.div>
        </div>

        <div className="order-1 lg:order-2 relative flex justify-center lg:justify-end">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            style={{ y: imgY, scale: imgScale }}
            className="relative w-full max-w-md lg:max-w-lg"
          >
            <div className="absolute -bottom-6 -right-6 w-full h-full bg-teal-dark" />
            <div className="relative bg-brown-dark p-5 sm:p-7">
              <img
                src={HERO_IMG}
                alt="Sócias Martins e Freitas Advogados Associados"
                className="relative w-full h-auto object-contain"
                data-testid="hero-image"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
